import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeOverflowItem - The overflow item shown when the More trigger is active.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);

    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }

    this.overflowMenu?.removeEventListener(
      'pointerleave',
      this.#overflowSubmenuListener
    );

    this.#cleanupMutationObserver();

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = {
    x: 0,
    y: 0,
  };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;

    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving =
      Math.abs(event.movementX) >= 1 ||
      Math.abs(event.movementY) >= 1;

    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);

    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;

    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);

      if (!target) return;

      const listItem = target.closest('.menu-list__list-item');

      if (
        listItem &&
        !listItem.contains(this.#state.activeItem)
      ) {
        listItem.dispatchEvent(
          new PointerEvent('pointerenter', {
            bubbles: false,
          })
        );
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener(
        'pointermove',
        this.#onPointerMove
      );
    }

    const rect = item.getBoundingClientRect();

    const isOverlap =
      this.headerComponent?.hasAttribute(
        'data-submenu-overlap-bottom-row'
      );

    const boundary = isOverlap
      ? this.headerComponent?.querySelector('.header__row--top')
      : this.headerComponent;

    item.style.setProperty(
      '--box-height',
      `${
        (boundary?.getBoundingClientRect().bottom ?? 0) -
        rect.top
      }px`
    );
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);

    this.#pointerIdleTimer = undefined;

    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector(
        '[part="overflow"]'
      )
    );
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return (
      this.refs.overflowMenu?.shadowRoot
        ?.querySelector('[part="overflow-list"]')
        ?.matches(':hover') ?? false
    );
  }

  /**
   * Find the first overflowing menu item shown by the More trigger.
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem =
      this.refs.overflowMenu?.querySelector(
        '[slot="overflow"] [ref="menuitem"]'
      );

    return menuItem instanceof HTMLElement
      ? menuItem
      : null;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (
      this.closest('header-component')
    );
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (
      !(event.target instanceof Element) ||
      !this.headerComponent
    ) {
      return;
    }

    const isMoreTrigger =
      event.target.slot === 'more';

    const item = findMenuItem(event.target);

    const overflowItem = isMoreTrigger
      ? this.#getFirstOverflowMenuItem()
      : null;

    if (
      !item ||
      item === this.#state.activeItem
    ) {
      return;
    }

    const isDefaultSlot =
      event.target.slot === '';

    this.dataset.overflowExpanded =
      (!isDefaultSlot).toString();

    const previouslyActiveItem =
      this.#state.activeItem;

    const previouslyActiveOverflowItem =
      this.#state.activeOverflowItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    if (
      previouslyActiveOverflowItem &&
      previouslyActiveOverflowItem !== previouslyActiveItem
    ) {
      previouslyActiveOverflowItem.ariaExpanded =
        'false';
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;

    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    if (
      overflowItem &&
      overflowItem !== item
    ) {
      overflowItem.ariaExpanded = 'true';
    }

    const overflowItemSubmenu =
      isMoreTrigger
        ? findSubmenu(overflowItem)
        : null;

    let submenu =
      findSubmenu(item) ||
      (overflowItemSubmenu
        ? this.overflowMenu
        : null);

    const hasSubmenu =
      Boolean(submenu);

    if (
      !hasSubmenu &&
      !isDefaultSlot
    ) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);

      this.#hoverDispatchTimer =
        undefined;

      const committedItem = item;

      if (event instanceof FocusEvent) {
        this.dispatchEvent(
          new MegaMenuHoverEvent()
        );
      } else {
        this.#hoverDispatchTimer =
          setTimeout(() => {
            this.#hoverDispatchTimer =
              undefined;

            if (
              this.#state.activeItem ===
              committedItem
            ) {
              this.dispatchEvent(
                new MegaMenuHoverEvent()
              );
            }
          }, HOVER_COMMIT_DELAY_MS);
      }

      submenu.dataset.active = '';

      this.#cleanupMutationObserver();

      this.#submenuMutationObserver =
        new MutationObserver(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (
                submenu.offsetHeight > 0
              ) {
                this.headerComponent?.style.setProperty(
                  '--submenu-height',
                  `${submenu.offsetHeight}px`
                );

                this.#cleanupMutationObserver();
              }
            });
          });
        });

      this.#submenuMutationObserver.observe(
        submenu,
        {
          childList: true,
          subtree: true,
        }
      );

      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight =
      submenu?.offsetHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight =
        this.#getOverflowListLinksHeight();

      if (hasSubmenu) {
        const overflowHeight =
          this.overflowMenu?.offsetHeight || 0;

        finalHeight = Math.max(
          overflowHeight,
          overflowListHeight
        );
      } else {
        finalHeight =
          overflowListHeight;
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    const headerVisibleHeight =
      this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty(
      '--submenu-height',
      `${finalHeight}px`
    );

    this.#setFullOpenHeaderHeight(
      finalHeight,
      headerVisibleHeight
    );

    this.style.setProperty(
      '--submenu-opacity',
      '1'
    );

    this.#startPointerTracking(
      item,
      previouslyActiveItem
    );
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (
      !(event.target instanceof Element)
    ) {
      return;
    }

    const menu =
      findSubmenu(
        this.#state.activeItem
      );

    const isMovingWithinMenu =
      event.relatedTarget instanceof Node &&
      menu?.contains(document.activeElement);

    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      event.type === 'blur' &&
      menu?.contains(event.relatedTarget);

    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element &&
      Boolean(
        event.relatedTarget.closest(
          '[slot="overflow"]'
        )
      );

    if (
      isMovingWithinMenu ||
      isMovingToOverflowMenu ||
      isMovingToSubmenu
    ) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(
          this.#state.activeItem
        );
      }

      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (
    item = this.#state.activeItem
  ) => {
    if (
      !item ||
      item !== this.#state.activeItem
    ) {
      return;
    }

    if (
      this.overflowListHovered ||
      this.overflowMenu?.matches(':hover')
    ) {
      return;
    }

    clearTimeout(this.#hoverDispatchTimer);

    this.#hoverDispatchTimer =
      undefined;

    this.headerComponent?.style.setProperty(
      '--submenu-height',
      '0px'
    );

    this.#setFullOpenHeaderHeight(
      0,
      0
    );

    this.style.setProperty(
      '--submenu-opacity',
      '0'
    );

    this.dataset.overflowExpanded =
      'false';

    const submenu =
      findSubmenu(item) ||
      (item.closest('[slot="more"]')
        ? this.overflowMenu
        : null);

    const activeOverflowItem =
      this.#state.activeOverflowItem;

    document.body.removeEventListener(
      'pointermove',
      this.#onPointerMove
    );

    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.activeOverflowItem =
      null;

    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (
      activeOverflowItem &&
      activeOverflowItem !== item
    ) {
      activeOverflowItem.ariaExpanded =
        'false';
    }

    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks =
      this.overflowMenu
        ?.querySelector('slot')
        ?.assignedElements();

    if (!slottedMenuLinks) {
      return (
        this.overflowMenu?.offsetHeight ||
        0
      );
    }

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach(
        (link) => {
          const submenu =
            /** @type {HTMLElement | null} */ (
              link.querySelector(
                '[ref="submenu[]"]'
              )
            );

          if (submenu) {
            cb(submenu);
          }
        }
      );
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty(
        'display',
        'none'
      );
    });

    const height =
      this.overflowMenu?.offsetHeight ||
      0;

    mapSubmenus((submenu) => {
      submenu.style.removeProperty(
        'display'
      );
    });

    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) {
      return 0;
    }

    const isOverlapSituation =
      this.headerComponent.hasAttribute(
        'data-submenu-overlap-bottom-row'
      );

    return (
      isOverlapSituation &&
      this.headerComponent.offsetHeight >
        0
        ? /** @type {HTMLElement | null} */ (
            this.headerComponent.querySelector(
              '.header__row--top'
            )
          )?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight
    );
  }

  /**
   * Calculate and set the full open header height.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(
    submenuHeight,
    headerVisibleHeight
  ) {
    if (!this.headerComponent) {
      return;
    }

    const nothingToOpen =
      submenuHeight === 0;

    const fullOpenHeaderHeight =
      nothingToOpen
        ? 0
        : submenuHeight +
          headerVisibleHeight;

    this.headerComponent.style.setProperty(
      '--full-open-header-height',
      `${fullOpenHeaderHeight}px`
    );
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images =
      this.querySelectorAll(
        'img[loading="lazy"]'
      );

    images?.forEach((image) => {
      image.removeAttribute('loading');
    });
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver =
      null;
  }
}

if (
  !customElements.get('header-menu')
) {
  customElements.define(
    'header-menu',
    HeaderMenu
  );
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (
    !(element instanceof Element)
  ) {
    return null;
  }

  return element.querySelector(
    '[ref="menuitem"]'
  );
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu =
    element?.parentElement?.querySelector(
      '[ref="submenu[]"]'
    );

  return submenu instanceof HTMLElement
    ? submenu
    : null;
}